/** Schema-only contract shared by Inspector, the domain API and PDF review UI. */
import * as Schema from "effect/Schema";

const Page = Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 500 }));
const ShortText = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500));
export const FindingPdfEdit = Schema.Struct({
  section: ShortText,
  beforePage: Page,
  afterPage: Page,
  beforeText: Schema.String.check(Schema.isMaxLength(6000)),
  afterText: Schema.String.check(Schema.isMaxLength(6000)),
}).check(Schema.makeFilter((edit) => edit.beforeText.trim() !== edit.afterText.trim()));
export type FindingPdfEdit = typeof FindingPdfEdit.Type;

/**
 * What must happen before (or after) a proposed fix is applied. A plain string
 * is the legacy form and reads as a `confirm` check. Typed checks say how the
 * reviewer meets them:
 *
 * - `fill`: a value the fix needs (a name, a date). The proposed text carries
 *   a `{{key}}` token where the value goes.
 * - `confirm`: an attestation the reviewer ticks.
 * - `approval`: someone (`approver`) must approve the amended document; the
 *   finding waits on that approval after the fix is applied.
 * - `followup`: work to do after applying, tracked as a task.
 * - `caveat`: a warning to read, nothing to tick.
 *
 * `blocking` is advice for the reviewer, never a gate: the checklist does not
 * stop Apply.
 */
export const FIX_CHECK_KINDS = ["fill", "confirm", "approval", "followup", "caveat"] as const;
export type FixCheckKind = (typeof FIX_CHECK_KINDS)[number];
export const FixCheckField = Schema.Struct({
  key: Schema.String.check(Schema.isPattern(/^[a-z][a-z0-9_]{0,39}$/)),
  label: ShortText,
  input: Schema.optional(Schema.Literals(["person", "text", "date"])),
  placeholder: Schema.optional(ShortText),
});
export type FixCheckField = typeof FixCheckField.Type;
export const TypedFixCheck = Schema.Struct({
  kind: Schema.Literals(FIX_CHECK_KINDS),
  text: ShortText,
  blocking: Schema.optional(Schema.Boolean),
  field: Schema.optional(FixCheckField),
  approver: Schema.optional(ShortText),
}).check(Schema.makeFilter((check) => (check.kind === "fill" ? check.field !== undefined : true)));
export type TypedFixCheck = typeof TypedFixCheck.Type;
export const OutstandingCheck = Schema.Union([ShortText, TypedFixCheck]);
export type OutstandingCheck = typeof OutstandingCheck.Type;

/** Any stored check in its typed form; a legacy string is a confirm check. */
export function typedFixCheck(check: OutstandingCheck): TypedFixCheck {
  return typeof check === "string" ? { kind: "confirm", text: check } : check;
}

/** The `{{key}}` tokens a proposed text still carries, in order of first use. */
export function fillTokens(text: string): ReadonlyArray<string> {
  return [...new Set([...text.matchAll(/\{\{([a-z][a-z0-9_]{0,39})\}\}/g)].map((match) => match[1]!))];
}

/**
 * A fill-in's stored value, read from the record's own keys only. Fill keys
 * match `^[a-z][a-z0-9_]{0,39}$`, so one may be named like an Object member
 * (`constructor`); an unset one must read as unset, not as the inherited
 * function.
 */
export function fillValue(fills: Readonly<Record<string, string>>, key: string): string | undefined {
  return Object.hasOwn(fills, key) ? fills[key] : undefined;
}

/** The text with each filled `{{key}}` replaced; unfilled tokens stay visible. */
export function applyFills(text: string, fills: Readonly<Record<string, string>>): string {
  return text.replace(/\{\{([a-z][a-z0-9_]{0,39})\}\}/g, (token, key: string) => {
    const value = fillValue(fills, key)?.trim();
    return value ? value : token;
  });
}

/**
 * The reviewer's record against one proposal's checklist: which checks were
 * ticked (by whom, when) and the values written into `{{key}}` fill-ins.
 * Keyed by the check's position in `outstandingChecks` (a proposal is
 * immutable, so positions are stable). Never a gate: Apply ignores it.
 */
export const FIX_FILL_MAX_LENGTH = 500;
export const FixCheckConfirmation = Schema.Struct({
  /** The member who ticked it; null when that account no longer exists. */
  byId: Schema.NullOr(Schema.String),
  /** Their name, or email when they have no name. */
  by: Schema.NullOr(Schema.String),
  at: Schema.String,
});
export type FixCheckConfirmation = typeof FixCheckConfirmation.Type;
export const FixReviewView = Schema.Struct({
  proposalId: Schema.String,
  confirmations: Schema.Record(Schema.String, FixCheckConfirmation),
  fills: Schema.Record(Schema.String, Schema.String),
});
export type FixReviewView = typeof FixReviewView.Type;
const FixCheckIndex = Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 19 }));
export const FixReviewWrite = Schema.Union([
  Schema.Struct({ action: Schema.Literal("confirm"), index: FixCheckIndex, confirmed: Schema.Boolean }),
  Schema.Struct({
    action: Schema.Literal("fill"),
    key: Schema.String.check(Schema.isPattern(/^[a-z][a-z0-9_]{0,39}$/)),
    /** An empty value clears the fill. */
    value: Schema.String.check(Schema.isMaxLength(FIX_FILL_MAX_LENGTH)),
  }),
]);
export type FixReviewWrite = typeof FixReviewWrite.Type;

/** The fill-in keys a proposal asks for, so a write cannot invent one. */
export function fixFillKeys(checks: ReadonlyArray<OutstandingCheck>): ReadonlySet<string> {
  return new Set(
    checks.flatMap((check) => {
      const typed = typedFixCheck(check);
      return typed.kind === "fill" && typed.field ? [typed.field.key] : [];
    }),
  );
}

/** A check is done when ticked, or (a fill-in) when its value is filled. */
export function isFixCheckDone(
  check: OutstandingCheck,
  index: number,
  review: Pick<FixReviewView, "confirmations" | "fills"> | null,
): boolean {
  if (!review) return false;
  if (review.confirmations[String(index)]) return true;
  const typed = typedFixCheck(check);
  return typed.kind === "fill" && typed.field ? Boolean(fillValue(review.fills, typed.field.key)?.trim()) : false;
}

/** "x of y" for the checklist header. */
export function fixChecklistProgress(
  checks: ReadonlyArray<OutstandingCheck>,
  review: Pick<FixReviewView, "confirmations" | "fills"> | null,
): { readonly done: number; readonly total: number } {
  return {
    done: checks.filter((check, index) => isFixCheckDone(check, index, review)).length,
    total: checks.length,
  };
}

export const FindingChangesDraft = Schema.Struct({
  sourceVersion: Schema.Int.check(Schema.isGreaterThan(0)),
  sourceSha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
  summary: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2000)),
  edits: Schema.Array(FindingPdfEdit).check(Schema.isMinLength(1), Schema.isMaxLength(24)),
  outstandingChecks: Schema.Array(OutstandingCheck).check(Schema.isMaxLength(20)),
});
export type FindingChangesDraft = typeof FindingChangesDraft.Type;
export const FindingChangesView = Schema.Struct({
  ...FindingChangesDraft.fields,
  id: Schema.String,
  findingId: Schema.String,
  policyId: Schema.String,
  sourceFilename: Schema.String,
  proposedFilename: Schema.String,
  proposedSha256: Schema.String,
  createdAt: Schema.String,
  createdByEmail: Schema.NullOr(Schema.String),
});
export type FindingChangesView = typeof FindingChangesView.Type;
export const FindingChangesSnapshot = Schema.Struct({
  proposal: Schema.NullOr(FindingChangesView),
});
export const FindingChangesUpload = Schema.Struct({
  ...FindingChangesDraft.fields,
  requestId: Schema.String.check(
    Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i),
  ),
  filename: ShortText,
  contentBase64: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4 * 1024 * 1024)),
});
export type FindingChangesUpload = typeof FindingChangesUpload.Type;

export const FindingChangesSource = Schema.Struct({
  sourceVersion: Schema.Int.check(Schema.isGreaterThan(0)),
  sourceSha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
  filename: Schema.String,
});

/** Amendment suggestions use the same PDF evidence, with their own scoped identity. */
const { findingId: _findingId, ...documentChangesFields } = FindingChangesView.fields;
export const RecommendationChangesView = Schema.Struct({
  ...documentChangesFields,
  recommendationId: Schema.String,
});
export type RecommendationChangesView = typeof RecommendationChangesView.Type;
export const RecommendationChangesSnapshot = Schema.Struct({
  proposal: Schema.NullOr(RecommendationChangesView),
});
export const DocumentChangesView = Schema.Union([FindingChangesView, RecommendationChangesView]);
export type DocumentChangesView = typeof DocumentChangesView.Type;
export const DocumentChangesSnapshot = Schema.Struct({
  proposal: Schema.NullOr(DocumentChangesView),
});
